import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  WorkspacePremium,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { GangFinancialSummary } from '../molecules/GangFinancialSummary';
import { ListGangsResponse } from '../../api/gangsApi';
import {
  GangCreationStep,
  gangParticipantFromGangMember,
} from '../../types/gangCreationTypes';
import { GangMemberListSection } from '../organisms/GangMemberListSection';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';

interface GangDetailsPanelProps {
  gang: ListGangsResponse['gangs'][number] | null;
  loading?: boolean;
  onEdit?: (gangId: string, step: GangCreationStep) => void;
  onDelete?: (gangId: string) => void;
  isDeleting?: boolean;
}

// Helper function to format currency (matching the summary step)
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const GangDetailsPanel: React.FC<GangDetailsPanelProps> = ({
  gang,
  loading = false,
  onEdit,
  onDelete,
  isDeleting = false,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  if (!gang && !loading) return null;

  const members = gang?.members ?? [];
  const leaders = members.filter(m => m.isLeader);
  const regularMembers = members.filter(m => !m.isLeader);
  const hasAnyMembers = members.length > 0;

  const leaderParticipants = leaders.map(member =>
    gangParticipantFromGangMember(member)
  );
  const memberParticipants = regularMembers.map(member =>
    gangParticipantFromGangMember(member)
  );

  const handleEdit = (step: GangCreationStep) => {
    if (gang && onEdit) {
      onEdit(gang.id, step);
    }
  };

  const renderTitle = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Typography
        variant="h5"
        color="text.primary"
        sx={{ fontWeight: 600, wordBreak: 'break-word' }}
        data-testid="gang-name-value"
        data-name={gang?.name ?? ''}
      >
        {gang ? gang.name : 'Gang Overview'}
      </Typography>
      {gang && onEdit ? (
        <Tooltip title="Edit name">
          <IconButton
            size="small"
            onClick={() => handleEdit(GangCreationStep.NAME)}
            data-testid="edit-section-name"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );

  return (
    <>
      {loading ? (
        <Box
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid="gang-details-loading"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        gang && (
          <Box sx={{ px: 3, pb: 3, pt: 1 }} data-testid="gang-details-content">
            {renderTitle()}

            {/* Financial Summary */}
            <Box data-testid="financial-summary-section">
              <GangFinancialSummary
                leaders={leaderParticipants}
                members={memberParticipants}
                isEqualSplit={gang.isEqualSplit}
                title="Financial Summary"
                showEditButton={Boolean(onEdit)}
                onEditClick={
                  onEdit
                    ? () => handleEdit(GangCreationStep.MEMBER_CONFIG)
                    : undefined
                }
                editButtonTooltip="Edit member configuration"
              />
              <Divider sx={{ my: 3 }} />
            </Box>

            {/* Empty members state */}
            {!hasAnyMembers && (
              <Alert
                severity="info"
                sx={{ mb: 3 }}
                data-testid="no-members-message"
              >
                This gang does not have any members yet.
              </Alert>
            )}

            <GangMemberListSection
              members={leaders}
              itemPrefix="leader"
              title={`Leader${leaders.length !== 1 ? 's' : ''}`}
              titleIcon={
                <WorkspacePremium
                  sx={{ fontSize: 20, color: 'warning.main' }}
                />
              }
              emptyMessage="No leaders assigned yet."
              avatarColor={theme => ({
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              })}
              formatCurrency={formatCurrency}
              onEdit={onEdit ? handleEdit : undefined}
              editStep={GangCreationStep.LEADERS}
              editTooltip="Edit leaders"
              testIdPrefix="leaders-"
            />

            <GangMemberListSection
              members={regularMembers}
              itemPrefix="member"
              title="Members"
              emptyMessage="No members assigned yet."
              avatarColor={theme => ({
                bgcolor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
              })}
              formatCurrency={formatCurrency}
              onEdit={onEdit ? handleEdit : undefined}
              editStep={GangCreationStep.MEMBERS}
              editTooltip="Edit members"
              testIdPrefix="members-"
            />

            {onDelete && (
              <>
                <Divider sx={{ my: 3 }} />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setIsDeleteDialogOpen(true)}
                  loading={isDeleting}
                  fullWidth
                  data-testid="delete-gang-button"
                >
                  Delete Gang
                </Button>

                <ConfirmDialog
                  open={isDeleteDialogOpen}
                  title="Delete Gang"
                  message={`Are you sure you want to delete "${gang.name}"? This action cannot be undone.`}
                  onCancel={() => setIsDeleteDialogOpen(false)}
                  onConfirm={() => {
                    setIsDeleteDialogOpen(false);
                    onDelete(gang.id);
                  }}
                  confirmText="Delete"
                  cancelText="Cancel"
                />
              </>
            )}
          </Box>
        )
      )}
    </>
  );
};

export default GangDetailsPanel;
